import React from 'react';

import Debug from '@/components/Debug';
import { Color } from '@/constants/color';
import { PersonChallenge } from '@/types/person_challenge';
import Badge from '@/ui/components/Badge';
import Card from '@/ui/components/Card';
import Heading from '@/ui/components/Heading';

interface ChallengeCardProps {
  challenge: PersonChallenge;
}

const PersonChallengeCard = ({ challenge }: ChallengeCardProps) => {
  const getStatusColor = (status: string | null): Color => {
    switch (status) {
      case 'finished':
        return Color.GREEN;
      case 'started':
        return Color.BLUE;
      case 'declined':
        return Color.RED;
      case 'not_defined':
        return Color.GRAY;
      case 'expired':
        return Color.YELLOW;
    }
    return Color.GRAY;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  const removeDashesAndCapitalizeCategory = (category: string): string => {
    return category
      .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  };

  return (
    <Card className="p-4 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <Heading className="text-lg font-semibold text-gray-900" text={challenge.challenge.title} />
        <Badge color={getStatusColor(challenge.status)}>
          {challenge.status
            ? challenge.status.charAt(0).toUpperCase() + challenge.status.slice(1)
            : 'Not Started'}
        </Badge>
      </div>

      <div className="space-y-2 text-sm text-gray-600">
        {challenge.challenge.category && (
          <div className="flex justify-between">
            <span className="font-medium">Category:</span>
            <span>{removeDashesAndCapitalizeCategory(challenge.challenge.category)}</span>
          </div>
        )}

        <div className="flex justify-between">
          <span className="font-medium">Treatment Week:</span>
          <span>{challenge.challenge.treatment_week}</span>
        </div>

        <div className="flex justify-between">
          <span className="font-medium">Treatment Day:</span>
          <span>{challenge.challenge.treatment_day}</span>
        </div>

        {challenge.started_at && (
          <div className="flex justify-between">
            <span className="font-medium">Started:</span>
            <span>{formatDate(challenge.started_at)}</span>
          </div>
        )}

        {challenge.finished_at && (
          <div className="flex justify-between">
            <span className="font-medium">Finished:</span>
            <span>{formatDate(challenge.finished_at)}</span>
          </div>
        )}

        {challenge.payout_totals &&
          (challenge.payout_totals.usd_in_cents > 0 ||
            challenge.payout_totals.exp_in_cents > 0) && (
            <div className="flex justify-between">
              <span className="font-medium">Payout:</span>
              <span>
                {challenge.payout_totals.usd_in_cents > 0 &&
                  `$${(challenge.payout_totals.usd_in_cents / 100).toFixed(2)}`}
                {challenge.payout_totals.exp_in_cents > 0 &&
                  ` ${challenge.payout_totals.exp_in_cents} exp`}
              </span>
            </div>
          )}

        {challenge.is_locked && <div className="text-orange-600 font-medium">🔒 Locked</div>}

        {challenge.blocked_by_parent && (
          <div className="text-red-600 font-medium">🚫 Blocked by parent challenge</div>
        )}
      </div>

      <Debug object={challenge as object} />
    </Card>
  );
};

export default PersonChallengeCard;
